import { createAsyncThunk } from '@reduxjs/toolkit'
import ACTIONS from '../../constants'
import { AppState } from '../../store'
import { etlAPI } from '../../../shared/api/ETLAPI'
import {
  EtlFileMappings,
  EtlFileCharacteristic,
  EtlQuestion,
  EtlValueMapping,
  EtlFinalCheckError,
} from '../../../shared/model/models'

const { ETL } = ACTIONS

export const setLatestEtlStatus = createAsyncThunk<any, void, { state: AppState }>(
  ETL.SET_ETL_STATUS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await etlAPI.getLatestEtlStatus()
    }
    return null
  },
)

export const setEtlFiles = createAsyncThunk<any, { runId: number | null }, { state: AppState }>(
  ETL.SET_ETL_FILES,
  async ({ runId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token && runId) {
      return await etlAPI.getEtlFileHeaders(runId)
    }
    return null
  },
)

export const setFileMappings = createAsyncThunk<
  any,
  { mappings: Record<string, EtlFileMappings> },
  { state: AppState }
>(ETL.SET_FILE_MAPPINGS, async ({ mappings }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.setFileMappings(latestEtlStatus.id, mappings)
    if (status === 200) return await etlAPI.getLatestEtlStatus()
    return null
  }
  return null
})

export const setFileCharacteristics = createAsyncThunk<
  any,
  { etlFileCharacteristic: EtlFileCharacteristic[] | null; isCompleted: boolean },
  { state: AppState }
>(ETL.SET_FILE_CHARACTERISTICS, async ({ etlFileCharacteristic, isCompleted }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.setFileCharacteristics(
      latestEtlStatus.id,
      etlFileCharacteristic,
      isCompleted,
    )
    if (status === 200) return await etlAPI.getLatestEtlStatus()
    return null
  }
  return null
})

export const setQuestions = createAsyncThunk<
  any,
  { additionalInfo: EtlQuestion[] | null; isCompleted: boolean },
  { state: AppState }
>(ETL.SET_QUESTIONS, async ({ additionalInfo, isCompleted }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.setQuestions(latestEtlStatus.id, additionalInfo, isCompleted)
    if (status === 200) return await etlAPI.getQuestions(latestEtlStatus.id)
    return null
  }
  return null
})

export const addQuestion = createAsyncThunk<any, void, { state: AppState }>(
  ETL.ADD_QUESTION,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      const status = await etlAPI.addQuestion(latestEtlStatus.id)
      if (status === 200) return await etlAPI.getQuestions(latestEtlStatus.id)
      return null
    }
    return null
  },
)

export const getFileMappings = createAsyncThunk<any, void, { state: AppState }>(
  ETL.GET_FILE_MAPPINGS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      return await etlAPI.getFileMappings(latestEtlStatus.id)
    }
    return null
  },
)

export const getFileCharacteristics = createAsyncThunk<any, void, { state: AppState }>(
  ETL.GET_FILE_CHARACTERISTICS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus, etlFiles },
    } = getState()
    if (token && latestEtlStatus && etlFiles) {
      const characteristics: EtlFileCharacteristic[] = await etlAPI.getEtlCharacteristics(
        latestEtlStatus.id,
      )
      if (Object.keys(etlFiles).length === 1) {
        characteristics.forEach((char) => {
          char.userFileName = Object.keys(etlFiles)[0]
        })
      }
      return characteristics
    }
    return null
  },
)

export const getQuestions = createAsyncThunk<any, void, { state: AppState }>(
  ETL.GET_QUESTIONS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      return await etlAPI.getQuestions(latestEtlStatus.id)
    }
    return null
  },
)

export const getVariables = createAsyncThunk<any, void, { state: AppState }>(
  ETL.GET_VARIABLES,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      return await etlAPI.getVariables(latestEtlStatus.id)
    }
    return null
  },
)

export const getOriginalValues = createAsyncThunk<
  any,
  { characteristicId: number },
  { state: AppState }
>(ETL.GET_ORIGINAL_VALUES, async ({ characteristicId }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const values = await etlAPI.getOriginalValues(latestEtlStatus.id, characteristicId)
    return {
      characteristicId,
      values: values.originalValues,
    }
  }
  return null
})

export const addVariable = createAsyncThunk<
  any,
  { variable: EtlValueMapping },
  { state: AppState }
>(ETL.ADD_VARIABLE, async ({ variable }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    return await etlAPI.addVariable(latestEtlStatus.id, variable)
  }
  return null
})

export const updateVariable = createAsyncThunk<
  any,
  { variable: EtlValueMapping },
  { state: AppState }
>(ETL.UPDATE_VARIABLE, async ({ variable }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    return await etlAPI.addVariable(latestEtlStatus.id, variable)
  }
  return null
})

export const deleteVariable = createAsyncThunk<
  any,
  { variable: EtlValueMapping },
  { state: AppState }
>(ETL.DELETE_VARIABLE, async ({ variable }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus && variable.id) {
    const status = await etlAPI.deleteVariable(latestEtlStatus.id, variable.id)
    if (status === 200) return variable
    return null
  }
  return null
})

export const deleteCharVariable = createAsyncThunk<
  any,
  { characteristicId: number },
  { state: AppState }
>(ETL.DELETE_CHAR_VARIABLE, async ({ characteristicId }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.deleteCharVariableGroup(latestEtlStatus.id, characteristicId)
    if (status === 200) return characteristicId
    return null
  }
  return null
})

export const updateEtlStep = createAsyncThunk<any, { step: string }, { state: AppState }>(
  ETL.UPDATE_ETL_STEP,
  async ({ step }, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      return await etlAPI.updateEtlStep(latestEtlStatus.id, step)
    }
    return null
  },
)

export const goBackETLStep = createAsyncThunk<any, void, { state: AppState }>(
  ETL.GO_BACK,
  async (_, { getState }) => {
    const {
      currentUser: { token },
      currentEtl: { latestEtlStatus },
    } = getState()
    if (token && latestEtlStatus) {
      return await etlAPI.goBackStep(latestEtlStatus.id)
    }
    return null
  },
)

export const getEtlFinalCheckErrors = createAsyncThunk<
  any,
  { reported: boolean },
  { state: AppState }
>(ETL.GET_FINAL_ERRORS, async ({ reported }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    return await etlAPI.getEtlFinalCheckErrors(latestEtlStatus.id, reported ? 1 : 0)
  }
  return null
})

export const updateEtlError = createAsyncThunk<
  any,
  { error: EtlFinalCheckError },
  { state: AppState }
>(ETL.UPDATE_ETL_ERROR, async ({ error }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.setEtlFinalCheckErrors(latestEtlStatus.id, [error])
    if (status === 200) return error
  }
  return null
})

export const reportEtlErrors = createAsyncThunk<
  any,
  { errors: EtlFinalCheckError[] },
  { state: AppState }
>(ETL.REPORT_ETL_ERRORS, async ({ errors }, { getState }) => {
  const {
    currentUser: { token },
    currentEtl: { latestEtlStatus },
  } = getState()
  if (token && latestEtlStatus) {
    const status = await etlAPI.setEtlFinalCheckErrors(latestEtlStatus.id, errors)
    if (status === 200) return await etlAPI.getEtlFinalCheckErrors(latestEtlStatus.id, 0)
  }
  return null
})
